
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { NAVIGATION_ITEMS } from '@/constants/navigation';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { motion } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { useMessages } from '@/hooks/useMessages';
import { getInitials } from '@/utils/stringUtils';
import { ROUTES } from '@/constants/routes';

const BottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const isMobile = useIsMobile();
  const { conversations, loading: messagesLoading } = useMessages();
  
  if (!user || !isMobile) return null;

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const totalUnreadMessages = conversations.reduce((total, conv) => total + conv.unread_count, 0);
  const isProfileActive = location.pathname === ROUTES.PROFILE;

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-[env(safe-area-inset-bottom,8px)] pt-2 bg-background/95 backdrop-blur-xl border-t border-border"
    >
      <div className="max-w-md mx-auto">
        <div className="flex justify-around items-center">
          {NAVIGATION_ITEMS.map((item) => {
            const active = isActive(item.path);
            
            return (
              <motion.button
                key={item.key}
                onClick={() => navigate(item.path)}
                whileTap={{ scale: 0.9 }}
                className={`relative flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all duration-200 min-w-[48px] ${
                  active
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <div className="relative">
                  <item.icon className="h-5 w-5 transition-all duration-200" />
                  
                  {active && (
                    <motion.div
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-primary rounded-full"
                    />
                  )}
                  
                  {item.key === 'messages' && totalUnreadMessages > 0 && !messagesLoading && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-2 -right-3 min-w-[16px] h-[16px] text-[9px] px-1 flex items-center justify-center rounded-full"
                    >
                      {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
                    </Badge>
                  )}
                </div>
                
                <span className={`text-[10px] mt-1.5 font-medium transition-all duration-200 ${
                  active ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  {t(item.labelKey)}
                </span>
              </motion.button>
            );
          })}

          {/* Profile Avatar */}
          <motion.button
            onClick={() => navigate(ROUTES.PROFILE)}
            whileTap={{ scale: 0.9 }}
            className={`relative flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-all duration-200 min-w-[48px] ${
              isProfileActive
                ? 'text-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="relative">
              <Avatar className={`h-5 w-5 transition-all duration-200 ${isProfileActive ? 'ring-2 ring-primary ring-offset-1 ring-offset-background' : ''}`}>
                <AvatarImage src={profile?.avatar_url || undefined} alt={profile?.full_name || 'User'} />
                <AvatarFallback className="text-[8px] bg-muted">
                  {getInitials(profile?.full_name || 'U')}
                </AvatarFallback>
              </Avatar>
              
              {isProfileActive && (
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-primary rounded-full"
                />
              )}
            </div>
            
            <span className={`text-[10px] mt-1.5 font-medium transition-all duration-200 ${
              isProfileActive ? 'text-primary' : 'text-muted-foreground'
            }`}>
              {t('nav.profile')}
            </span>
          </motion.button>
        </div>
      </div>
    </nav>
  );
};

export default BottomNavigation;
